// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Archive, 
  Search, 
  Filter, 
  Building2, 
  ChevronRight,
  ChevronLeft,
  FileText,
  Clock,
  CheckCircle,
  Eye,
  ExternalLink,
  Layers,
  X
} from "lucide-react";
import { createPortal } from "react-dom";

// FIREBASE & CONTEXT
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../config/firebase"; 
import { useAuth } from "../../contexts/AuthContext"; 

const HistoricoLocacoesPage = () => {
  const { companyInfo, userInfo } = useAuth();
  
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // 🟢 Isolamento de Dados: Admin vê tudo, Imob vê apenas as suas
  const isAdmin = userInfo?.role === 'superadmin' || userInfo?.isInternal;

  // Filtros (Apenas visível para Admin)
  const [imobiliariaFilter, setImobiliariaFilter] = useState("Todas");
  const [imobiliariasList, setImobiliariasList] = useState([]);

  // States de Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // States do Modal de Documentos (Auditoria)
  const [isModalDocsOpen, setIsModalDocsOpen] = useState(false);
  const [garantiaSelecionada, setGarantiaSelecionada] = useState(null);

  // ============================================================================
  // BUSCA GLOBAL DE DADOS (ACL APLICADO)
  // ============================================================================
  useEffect(() => {
    // Se for imobiliária, precisa ter o ID
    if (!isAdmin && !companyInfo?.id) return;

    let qContracts;

    if (isAdmin) {
      // 👑 ADMIN: Busca TODO o Histórico (Onde status é "Contrato Encerrado")
      qContracts = query(
        collection(db, "contratos_ativos"),
        where("status", "==", "Contrato Encerrado")
      );
    } else {
      // 🏢 IMOBILIÁRIA: Busca APENAS o seu Histórico (Onde status é "Contrato Encerrado")
      qContracts = query(
        collection(db, "contratos_ativos"),
        where("companyId", "==", companyInfo.id),
        where("status", "==", "Contrato Encerrado")
      );
    }

    const unsubContracts = onSnapshot(qContracts, (snapshot) => {
      const contracts = [];
      const imobs = new Set();
      
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        contracts.push({ id: docSnap.id, ...data });
        if (isAdmin && data.companyName) {
          imobs.add(data.companyName);
        }
      });
      
      // Ordena pelos mais recentemente encerrados
      contracts.sort((a, b) => {
        const dateA = a.dataEncerramentoDefinitivo?.toMillis() || 0;
        const dateB = b.dataEncerramentoDefinitivo?.toMillis() || 0;
        return dateB - dateA;
      });

      setHistorico(contracts);
      if (isAdmin) setImobiliariasList(Array.from(imobs).sort());
      setLoading(false);
    });

    return () => unsubContracts();
  }, [isAdmin, companyInfo?.id]);

  // Reseta a página ao buscar ou filtrar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage, imobiliariaFilter]);

  // ============================================================================
  // LÓGICA DO MODAL DE AUDITORIA DE DOCUMENTOS
  // ============================================================================
  const abrirModalDocs = (garantia) => {
    setGarantiaSelecionada(garantia);
    setIsModalDocsOpen(true);
  };

  const fecharModalDocs = () => {
    setIsModalDocsOpen(false);
    setGarantiaSelecionada(null);
  };

  const handleAbrirTodosDocs = () => {
    alert("Dica: Os navegadores bloqueiam a abertura de múltiplas abas ao mesmo tempo. Caso os documentos não abram, verifique o bloqueador de pop-ups na sua barra de endereços e clique em 'Sempre permitir'.");
    // Futura integração com as URLs reais do Storage.
    console.log("Abrindo documentos...");
  };

  // Trava de Scroll
  useEffect(() => {
    if (isModalDocsOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [isModalDocsOpen]);

  // ============================================================================
  // FILTRO E PAGINAÇÃO
  // ============================================================================
  const filteredContracts = historico.filter(contrato => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      contrato.pagador?.nome?.toLowerCase().includes(searchLower) ||
      contrato.pagador?.cpf?.includes(searchTerm) ||
      contrato.protocoloOrigem?.toLowerCase().includes(searchLower);

    const matchesImob = isAdmin && imobiliariaFilter !== "Todas" ? contrato.companyName === imobiliariaFilter : true;

    return matchesSearch && matchesImob;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredContracts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);

  const nextPage = () => { if (currentPage < totalPages) setCurrentPage(prev => prev + 1); };
  const prevPage = () => { if (currentPage > 1) setCurrentPage(prev => prev - 1); };

  // ============================================================================
  // SKELETON LOADER
  // ============================================================================
  if (loading) {
    return (
      <div className="p-4 sm:p-5 xl:p-8 w-full animate-pulse bg-[#f8fafc] min-h-screen space-y-6">
        <div className="flex items-center gap-4 mb-6">
           <div className="w-14 h-14 bg-slate-200 rounded-2xl shrink-0"></div>
           <div className="space-y-2"><div className="h-6 bg-slate-200 rounded w-64"></div><div className="h-4 bg-slate-200 rounded w-96"></div></div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-200 h-16"></div>
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="flex justify-between items-center p-6 border-b border-slate-50">
              <div className="space-y-2"><div className="h-4 bg-slate-200 rounded w-24"></div><div className="h-3 bg-slate-100 rounded w-16"></div></div>
              <div className="space-y-2"><div className="h-4 bg-slate-200 rounded w-48"></div><div className="h-3 bg-slate-100 rounded w-32"></div></div>
              <div className="h-8 bg-slate-200 rounded w-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-5 xl:p-8 w-full font-sans animate-fade-in bg-[#f8fafc] min-h-full space-y-6 relative">
      
      {/* BREADCRUMB E HEADER */}
      <div className="mb-2">
        <div className="flex items-center text-sm text-slate-500 font-medium mb-2">
          <Link to={isAdmin ? "/super-admin" : "/dashboard"} className="hover:text-brand-primary transition-colors">Dashboard</Link>
          <ChevronRight className="w-4 h-4 mx-1" />
          <span className="text-slate-800 font-semibold">Histórico de Locações</span>
        </div>
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center shrink-0 border border-slate-200 shadow-sm">
              <Archive className="w-7 h-7 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
                Histórico de Locações
              </h1>
              <p className="text-sm font-medium text-slate-500 mt-1">
                Conformidade jurídica e arquivo morto de garantias encerradas.
              </p>
            </div>
          </div>

          <div className="bg-white px-5 py-3 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center shrink-0">
              <Archive className="w-5 h-5 text-slate-500" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Total Arquivado</p>
              <p className="text-xl font-bold text-slate-800">{filteredContracts.length} contratos</p>
            </div>
          </div>
        </div>
      </div>

      {/* ÁREA DE BUSCA E FILTROS */}
      <div className="bg-white p-4 xl:p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="relative w-full md:max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="w-4 h-4 text-slate-400" />
          </div>
          <input 
            type="text"
            placeholder="Buscar por nome do inquilino, CPF ou protocolo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary transition-shadow"
          />
        </div>

        {isAdmin && (
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-600 bg-slate-50 px-3 py-2.5 rounded-xl border border-slate-200">
              <Filter className="w-4 h-4" />
              <select 
                value={imobiliariaFilter}
                onChange={(e) => setImobiliariaFilter(e.target.value)}
                className="bg-transparent focus:outline-none cursor-pointer max-w-[200px] truncate"
              >
                <option value="Todas">Todas as Imobiliárias</option>
                {imobiliariasList.map(imob => (
                  <option key={imob} value={imob}>{imob}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* TABELA DO HISTÓRICO */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto min-h-[400px] flex-1 custom-scrollbar">
          <table className="w-full text-left border-collapse whitespace-nowrap">
            <thead>
              <tr className="text-xs font-semibold text-slate-500 border-b border-slate-100 bg-slate-50 uppercase tracking-wider">
                <th className="px-6 py-4">Protocolo / Encerramento</th>
                {isAdmin && <th className="px-6 py-4">Imobiliária</th>}
                <th className="px-6 py-4">Locatário (Ex-Cliente)</th>
                <th className="px-6 py-4">Status Final</th>
                <th className="px-6 py-4 text-right">Auditoria</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 5 : 4} className="py-20 text-center">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Archive className="w-8 h-8 text-slate-300" />
                    </div>
                    <p className="text-slate-800 font-bold text-base">Nenhum contrato arquivado encontrado.</p>
                    <p className="text-slate-500 text-sm mt-1">Garantias encerradas aparecerão aqui para consultas jurídicas.</p>
                  </td>
                </tr>
              ) : (
                currentItems.map((contrato) => (
                  <tr key={contrato.id} className="transition-colors hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <p className="font-bold text-slate-800 text-sm">{contrato.protocoloOrigem}</p>
                      <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3"/> 
                        {contrato.dataEncerramentoDefinitivo 
                          ? new Date(contrato.dataEncerramentoDefinitivo.toDate()).toLocaleDateString('pt-BR') 
                          : "Data Indisponível"}
                      </p>
                    </td>
                    
                    {isAdmin && (
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="font-medium text-slate-700 max-w-[180px] xl:max-w-[250px] truncate" title={contrato.companyName}>
                            {contrato.companyName || "Não informada"}
                          </span>
                        </div>
                      </td>
                    )}

                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-800 text-sm max-w-[200px] truncate" title={contrato.pagador?.nome}>
                        {contrato.pagador?.nome || "Não informado"}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">CPF: {contrato.pagador?.cpf || "N/A"}</p>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-slate-600 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200 font-semibold text-xs w-max">
                        <CheckCircle className="w-3.5 h-3.5" /> Arquivado
                      </div>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button 
                          onClick={() => abrirModalDocs(contrato)}
                          className="inline-flex items-center justify-center p-2 text-slate-500 hover:text-brand-primary bg-slate-50 hover:bg-brand-primary/10 border border-slate-200 hover:border-brand-primary/30 rounded-lg transition-colors shadow-sm"
                          title="Auditoria de Documentos"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        
                        {isAdmin && (
                          <Link 
                            to={`/super-admin/request/${(contrato.protocoloOrigem || contrato.protocolo || "").replace(/\//g, '-')}`}
                            className="inline-flex items-center justify-center p-2 text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors"
                            title="Ver Ficha Original"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* CONTROLES DE PAGINAÇÃO VIVOS (FOOTER) */}
        {filteredContracts.length > 0 && (
          <div className="p-4 sm:p-5 border-t border-slate-100 bg-slate-50 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500 shrink-0">
            <span className="font-medium text-center md:text-left">
              Mostrando <strong className="text-slate-800">{indexOfFirstItem + 1}</strong> a <strong className="text-slate-800">{Math.min(indexOfLastItem, filteredContracts.length)}</strong> de <strong className="text-slate-800">{filteredContracts.length}</strong> arquivados
            </span>
            
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm w-full sm:w-auto justify-center">
                <span className="font-medium">Itens:</span>
                <select 
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="bg-transparent border-none font-bold text-brand-primary focus:ring-0 cursor-pointer text-sm outline-none"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-2 justify-center w-full sm:w-auto">
                  <button 
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:hover:bg-white transition-colors shadow-sm"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm font-bold text-slate-700 px-3 py-1 bg-white border border-slate-200 rounded-lg shadow-sm">
                    {currentPage} / {totalPages}
                  </span>
                  <button 
                    onClick={nextPage}
                    disabled={currentPage >= totalPages}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:hover:bg-white transition-colors shadow-sm"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* MODAL: AUDITORIA DE DOCUMENTOS DE ENCERRAMENTO */}
      {isModalDocsOpen && garantiaSelecionada && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6 animate-fade-in">
           <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={fecharModalDocs}></div>
           <div className="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
              <div className="p-6 border-b border-slate-200 flex justify-between items-start sm:items-center bg-white shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Archive className="w-5 h-5 text-slate-500 shrink-0"/> Arquivo Morto
                  </h2>
                  <p className="text-sm text-slate-500 font-medium mt-1">Dossiê de Encerramento - {garantiaSelecionada.protocoloOrigem}</p>
                </div>
                <button onClick={fecharModalDocs} className="p-2 bg-slate-50 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                  <X className="w-5 h-5"/>
                </button>
              </div>
              
              <div className="p-6 bg-slate-50 space-y-4">
                <div className="bg-slate-100 border border-slate-200 text-slate-600 text-sm p-4 rounded-xl font-medium">
                  Documentos probatórios anexados pela imobiliária no momento da extinção legal da garantia.
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border border-slate-200 rounded-xl bg-white shadow-sm gap-4 hover:border-brand-primary/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-800 truncate">{garantiaSelecionada.documentosEncerramento?.boletoComprovante?.nome || "Boleto e Comprovante.pdf"}</p>
                    <span className="inline-block mt-1.5 px-2 py-1 bg-slate-100 text-[10px] font-bold text-slate-500 rounded uppercase border border-slate-200">Boleto + Comprovante</span>
                  </div>
                  <button 
                    onClick={() => garantiaSelecionada.documentosEncerramento?.boletoComprovante?.url && window.open(garantiaSelecionada.documentosEncerramento.boletoComprovante.url, '_blank')}
                    disabled={!garantiaSelecionada.documentosEncerramento?.boletoComprovante?.url}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-primary/10 text-brand-primary hover:bg-brand-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors font-semibold text-sm w-full sm:w-auto justify-center"
                  >
                    Visualizar <ExternalLink className="w-4 h-4"/>
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border border-slate-200 rounded-xl bg-white shadow-sm gap-4 hover:border-brand-primary/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-800 truncate">{garantiaSelecionada.documentosEncerramento?.reciboChaves?.nome || "Recibo_Chaves.pdf"}</p>
                    <span className="inline-block mt-1.5 px-2 py-1 bg-slate-100 text-[10px] font-bold text-slate-500 rounded uppercase border border-slate-200">Recibo de Chaves</span>
                  </div>
                  <button 
                    onClick={() => garantiaSelecionada.documentosEncerramento?.reciboChaves?.url && window.open(garantiaSelecionada.documentosEncerramento.reciboChaves.url, '_blank')}
                    disabled={!garantiaSelecionada.documentosEncerramento?.reciboChaves?.url}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-primary/10 text-brand-primary hover:bg-brand-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors font-semibold text-sm w-full sm:w-auto justify-center"
                  >
                    Visualizar <ExternalLink className="w-4 h-4"/>
                  </button>
                </div>
              </div>

              <div className="p-6 border-t border-slate-200 bg-white flex justify-between items-center shrink-0">
                <button 
                  onClick={handleAbrirTodosDocs}
                  className="px-4 py-2.5 text-sm font-semibold text-teal-700 bg-teal-50 border border-teal-200 hover:bg-teal-100 rounded-full flex items-center gap-2 transition-colors shadow-sm"
                >
                  <Layers className="w-4 h-4"/> Abrir todos de uma única vez
                </button>
                <button onClick={fecharModalDocs} className="px-6 py-2.5 text-sm font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-full transition-colors border border-slate-200 shadow-sm">
                  Fechar
                </button>
              </div>
           </div>
        </div>
      , document.body)}

    </div>
  );
};

export default HistoricoLocacoesPage;